<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.productCategory.actions.delete.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.confirm'), type: 'confirm', variant: 'danger', callback: confirm },
    ]"
    :portal="portal"
  >
    <div class="-mx-5 px-5 border-t border-b bg-gray-50 py-4">
      <p class="text-sm text-gray-800 mb-5 font-semibold" v-html="$t('components.productCategory.actions.delete.whatShouldWeDo')" />
      <o-field-group :loading="fetching">
        <select-field
          v-model="value"
          :options="options"
          :loading="fetching"
        />
      </o-field-group>

      <o-field-group
        v-if="value === 'delete'"
        :label="$t('components.productCategory.actions.delete.confirmRemove')"
        :info="$t('components.productCategory.actions.delete.confirmRemoveInfo')"
        col
        reverse
        class="mt-4 block"
      >
        <checkbox-field
          v-model="confirmDelete"
        />
      </o-field-group>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { OFieldGroup } from '@/components/elements'
import { SelectField, CheckboxField } from '@/components/fields'

import type { ProductCategory } from '~/_api/models/productCategory'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification, $bus } = useNuxtApp()

const dialog = ref<any>()
const productCategory = ref<ProductCategory | null>(null)
const value = ref<ProductCategory | 'delete' | null>(null)
const fetching = ref(false)
const productCategories = ref<ProductCategory[] | null>(null)
const confirmDelete = ref(false)

const info = computed(() => {
  return $i18n.t('components.productCategory.actions.delete.info', {
    name: productCategory.value?.name,
    id: productCategory.value?.idProductCategory,
  })
})

const options = computed(() => {
  const categories = productCategories.value
  const opts: Array<{ label: string; value: ProductCategory | 'delete' | null }> = [
    { label: $i18n.t('general.noCategory') as string, value: null },
    { label: $i18n.t('components.productCategory.actions.delete.deleteProducts') as string, value: 'delete' },
  ]

  if (categories) {
    for (const category of categories) {
      opts.push({
        label: category.name,
        value: category,
      })
    }
  }

  return opts
})

const disabled = computed(() => {
  if (value.value === 'delete' && confirmDelete.value === false) {
    return true
  }
  return false
})

const open = (category: ProductCategory) => {
  productCategory.value = category
  value.value = null
  confirmDelete.value = false

  dialog.value.open()

  fetchProductCategories()
}

const confirm = async () => {
  if (!productCategory.value) return

  emit('update:loading', true)

  try {
    const data: Record<string, any> = {}

    if (value.value) {
      if (value.value === 'delete') {
        data.deleteAllProducts = true
      } else {
        data.moveToCategory = value.value.idProductCategory
      }
    }

    const response = await $api.productCategory.deleteWithOptions(productCategory.value, data)
    emit('update:loading', false)

    if (response.success) {
      $notification.toast({
        title: $i18n.t('components.productCategory.actions.delete.success'),
        text: $i18n.t('components.productCategory.actions.delete.successInfo', { name: productCategory.value.name }),
        variant: 'danger',
      })

      emit('success')
      $bus.emit.deleted('ProductCategory', productCategory.value)

      return true
    }
  } catch (e: any) {
    $notification.toast({
      title: $i18n.t('components.productCategory.actions.delete.error'),
      text: $i18n.t('components.productCategory.actions.delete.errorInfo', { name: productCategory.value?.name }),
    })

    emit('error')
  }
}

const fetchProductCategories = async () => {
  fetching.value = true

  try {
    const response = await $api.productCategory.list()
    productCategories.value = response.items
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e.errors })
  }

  fetching.value = false
}

defineExpose({ open })
</script>
